import { createElement } from "react";
import type { ReactNode } from "react";
import { DEBUG_MODE } from '../constants';

const logFn = (...args: any[]) => DEBUG_MODE && console.log('🔵', ...args);
const warnFn = (...args: any[]) => DEBUG_MODE && console.warn('🟠', ...args);
const errorFn = (...args: any[]) => DEBUG_MODE && console.error('🔴', ...args);

export const debugConsole = {
  log: logFn,
  warn: warnFn,
  error: errorFn
};

export const debugRun = (fn: (console: typeof debugConsole) => void) => {
  if (DEBUG_MODE) {
    fn(debugConsole);
  }
};

const prettyJSONStyles = {
  key: {
    color: '#7c3aed',
    fontWeight: 700,
  },
  string: {
    color: '#047857',
  },
  number: {
    color: '#dc2626',
    fontWeight: 600,
  },
  boolean: {
    color: '#2563eb',
    fontWeight: 600,
  },
  null: {
    color: '#6b7280',
    fontStyle: 'italic',
  },
  array: {
    color: '#d97706',
    fontWeight: 700,
  },
  object: {
    color: '#374151',
    fontWeight: 700,
  },
  punctuation: {
    color: '#6b7280',
  },
  omitted: {
    color: '#9ca3af',
    fontStyle: 'italic',
  },
} as const;

const JSON_INDENT = '  ';

interface FilteredJSONValue {
  value: any;
  includeAll: boolean;
  children?: Array<{
    key: string | number;
    value: FilteredJSONValue;
    originalIndex: number;
  }>;
}

const token = (
  type: keyof typeof prettyJSONStyles,
  value: string,
  key: string
) => createElement('span', {
  key,
  style: prettyJSONStyles[type],
}, value);

const stringifyToken = (value: string) => JSON.stringify(value);

const isPlainObject = (value: any) => (
  value !== null
  && typeof value === 'object'
  && !Array.isArray(value)
);

const normalizeSearch = (search?: string): string => (
  search || ''
).trim().toLowerCase();

const matchesSearch = (
  value: any,
  search: string,
): boolean => String(value)
  .toLowerCase()
  .includes(search);

const hasSearch = (search: string): boolean => search.length > 0;

const getFilteredJSONValue = (
  value: any,
  search: string,
): FilteredJSONValue | null => {
  if (!hasSearch(search)) {
    return {
      value,
      includeAll: true,
    };
  }

  if (Array.isArray(value)) {
    const children = value
      .map((item, index) => ({
        key: index,
        value: getFilteredJSONValue(item, search),
        originalIndex: index,
      }))
      .filter(child => child.value !== null) as FilteredJSONValue['children'];

    return children && children.length
      ? {
        value,
        includeAll: false,
        children,
      }
      : null;
  }

  if (isPlainObject(value)) {
    const children = Object
      .entries(value)
      .map(([key, item], index) => {
        const keyMatches = matchesSearch(key, search);
        const filteredValue = keyMatches
          ? {
            value: item,
            includeAll: true,
          }
          : getFilteredJSONValue(item, search);

        return {
          key,
          value: filteredValue,
          originalIndex: index,
        };
      })
      .filter(child => child.value !== null) as FilteredJSONValue['children'];

    return children && children.length
      ? {
        value,
        includeAll: false,
        children,
      }
      : null;
  }

  return matchesSearch(value, search)
    ? {
      value,
      includeAll: true,
    }
    : null;
};

const omittedLine = (
  depth: number,
  key: string,
  withComma: boolean
): ReactNode[] => [
  JSON_INDENT.repeat(depth),
  token('omitted', '...', `${key}-ellipsis`),
  withComma ? token('punctuation', ',', `${key}-ellipsis-comma`) : '',
  '\n',
];

const withOmittedMarkers = (
  children: NonNullable<FilteredJSONValue['children']>,
  originalLength: number,
  depth: number,
  keyPrefix: string,
  renderChild: (
    child: NonNullable<FilteredJSONValue['children']>[number],
    index: number,
    isLast: boolean
  ) => ReactNode[]
): ReactNode[] => {
  const result: ReactNode[] = [];

  children.forEach((child, index) => {
    const previousOriginalIndex = index > 0
      ? children[index - 1].originalIndex
      : -1;
    const hasOmittedBefore = child.originalIndex > previousOriginalIndex + 1;

    if (hasOmittedBefore) {
      result.push(...omittedLine(
        depth,
        `${keyPrefix}-omitted-before-${child.originalIndex}`,
        previousOriginalIndex !== -1
      ));
    }

    result.push(...renderChild(child, index, index === children.length - 1));
  });

  const lastChild = children[children.length - 1];

  if (lastChild && lastChild.originalIndex < originalLength - 1) {
    result.push(...omittedLine(
      depth,
      `${keyPrefix}-omitted-after-${lastChild.originalIndex}`,
      false
    ));
  }

  return result;
};

const formatPrimitive = (
  value: any,
  key: string,
): ReactNode[] => {
  if (typeof value === 'string') {
    return [token('string', stringifyToken(value), key)];
  }

  if (typeof value === 'number') {
    return [token('number', String(value), key)];
  }

  if (typeof value === 'boolean') {
    return [token('boolean', String(value), key)];
  }

  if (value === null) {
    return [token('null', 'null', key)];
  }

  return [token('string', stringifyToken(String(value)), key)];
};

const formatJSONValue = (
  value: any,
  depth: number,
  keyPrefix: string,
  filteredValue?: FilteredJSONValue,
): ReactNode[] => {
  const valueToFormat = filteredValue?.value ?? value;
  const shouldFormatAll = filteredValue?.includeAll ?? true;

  if (Array.isArray(valueToFormat)) {
    if (!valueToFormat.length) {
      return [
        token('array', '[', `${keyPrefix}-array-open`),
        token('array', ']', `${keyPrefix}-array-close`),
      ];
    }

    return [
      token('array', '[', `${keyPrefix}-array-open`),
      '\n',
      ...(shouldFormatAll
        ? valueToFormat.flatMap((item, index) => [
          JSON_INDENT.repeat(depth + 1),
          ...formatJSONValue(item, depth + 1, `${keyPrefix}-${index}`),
          index < valueToFormat.length - 1
            ? token('punctuation', ',', `${keyPrefix}-${index}-comma`)
            : '',
          '\n',
        ])
        : withOmittedMarkers(
          filteredValue?.children || [],
          valueToFormat.length,
          depth + 1,
          keyPrefix,
          (child, index, isLast) => [
            JSON_INDENT.repeat(depth + 1),
            ...formatJSONValue(child.value.value, depth + 1, `${keyPrefix}-${child.key}`, child.value),
            !isLast || child.originalIndex < valueToFormat.length - 1
              ? token('punctuation', ',', `${keyPrefix}-${child.key}-comma`)
              : '',
            '\n',
          ]
        )),
      JSON_INDENT.repeat(depth),
      token('array', ']', `${keyPrefix}-array-close`),
    ];
  }

  if (isPlainObject(valueToFormat)) {
    const entries = Object.entries(valueToFormat);

    if (!entries.length) {
      return [
        token('object', '{', `${keyPrefix}-object-open`),
        token('object', '}', `${keyPrefix}-object-close`),
      ];
    }

    return [
      token('object', '{', `${keyPrefix}-object-open`),
      '\n',
      ...(shouldFormatAll
        ? entries.flatMap(([key, item], index) => [
          JSON_INDENT.repeat(depth + 1),
          token('key', stringifyToken(key), `${keyPrefix}-${key}-key`),
          token('punctuation', ': ', `${keyPrefix}-${key}-colon`),
          ...formatJSONValue(item, depth + 1, `${keyPrefix}-${key}`),
          index < entries.length - 1
            ? token('punctuation', ',', `${keyPrefix}-${key}-comma`)
            : '',
          '\n',
        ])
        : withOmittedMarkers(
          filteredValue?.children || [],
          entries.length,
          depth + 1,
          keyPrefix,
          (child, index, isLast) => [
            JSON_INDENT.repeat(depth + 1),
            token('key', stringifyToken(String(child.key)), `${keyPrefix}-${child.key}-key`),
            token('punctuation', ': ', `${keyPrefix}-${child.key}-colon`),
            ...formatJSONValue(child.value.value, depth + 1, `${keyPrefix}-${child.key}`, child.value),
            !isLast || child.originalIndex < entries.length - 1
              ? token('punctuation', ',', `${keyPrefix}-${child.key}-comma`)
              : '',
            '\n',
          ]
        )),
      JSON_INDENT.repeat(depth),
      token('object', '}', `${keyPrefix}-object-close`),
    ];
  }

  return formatPrimitive(valueToFormat, `${keyPrefix}-primitive`);
};

export const prettyJSON = (
  json: any,
  search?: string
): ReactNode[] => {
  const normalizedSearch = normalizeSearch(search);
  const filteredValue = getFilteredJSONValue(json, normalizedSearch);

  return filteredValue
    ? formatJSONValue(json, 0, 'json', filteredValue)
    : [
      token('omitted', '...', 'json-empty-search'),
    ];
};
